/**
 * @file The CameraApi is a property of the ViewportApi and must be used by all corresponding ViewportApis.
 *       It is used for all camera related functionality.
 *
 * @module CameraApiDefault
 * @author Michael Oppitz
 */

let CameraApi = function (___api, ___refs) {

  const CameraApiInterface = require('../../../interfaces/api/CameraApiInterface');

  let _api = ___api,
      _threeDManager = ___refs.threeDManager,
      _apiResponse = ___refs.apiResponse;

  /**
   * @extends module:CameraApiInterface~CameraApiInterface
   * @lends module:CameraApiDefault~CameraApi
   */
  class CameraApi extends CameraApiInterface {
    /**
     * The camera type, see {@link module:CameraApiDefault~CameraApi#TYPE} for values
     *
     * @typedef {Number} module:CameraApiDefault~CameraApi#CameraType
     */

    /**
     * ### ShapeDiver Viewer - Camera API
     *
     * @constructs module:CameraApiDefault~CameraApi
     */
    constructor() {
      super();

      /**
        * Enum for camera types.
        * @readonly
        * @enum {module:CameraApiDefault~CameraApi#CameraType}
        */
      this.TYPE = {
        /** perspective camera */
        PERSPECTIVE: 0,
        /** orthographic camera, top view */
        TOP: 1,
        /** orthographic camera, bottom view */
        BOTTOM: 2,
        /** orthographic camera, right view */
        RIGHT: 3,
        /** orthographic camera, left view */
        LEFT: 4,
        /** orthographic camera, back view */
        BACK: 5,
        /** orthographic camera, front view */
        FRONT: 6,
        /** AR camera */
        AR: 7
      };

      this.get = this.get.bind(this);

      this.updateAsync = this.updateAsync.bind(this);
      this.animateAsync = this.animateAsync.bind(this);
      this.zoomAsync = this.zoomAsync.bind(this);
      this.resetAsync = this.resetAsync.bind(this);
    }

    /** @inheritdoc */
    get() {
      return _apiResponse(null, _threeDManager.cameraHandler.getPositionAndTarget());
    }

    /** @inheritdoc */
    updateAsync(camera, transition) {
      return _threeDManager.cameraHandler.setPositionAndTarget(
        camera.position,
        camera.target,
        transition
      ).then(function (bSuccess) {
        if (bSuccess)
          return _apiResponse(null, _threeDManager.cameraHandler.getPositionAndTarget());
        else
          return _apiResponse('Setting new camera target and position failed', _threeDManager.cameraHandler.getPositionAndTarget());
      });
    }

    /** @inheritdoc */
    animateAsync(path, transition) {
      if (!Array.isArray(path)) return Promise.resolve(_apiResponse('First parameter must be an array of CameraDefinitions'));
      let positions = [], targets = [];
      path.forEach((p) => {
        positions.push(p.position);
        targets.push(p.target);
      });
      return _threeDManager.cameraHandler.setCameraPath(positions, targets, transition).then(function (bSuccess) {
        if (bSuccess)
          return _apiResponse(null, _threeDManager.cameraHandler.getPositionAndTarget());
        else
          return _apiResponse('Camera path animation failed', _threeDManager.cameraHandler.getPositionAndTarget());
      });
    }

    /** @inheritdoc */
    zoomAsync(scenePaths, transition) {
      return _threeDManager.cameraHandler.zoomExtents(
        transition, _threeDManager.computeBoundingBox(scenePaths)
      ).then(function (bSuccess) {
        if (bSuccess)
          return _apiResponse(null, _threeDManager.cameraHandler.getPositionAndTarget());
        else
          return _apiResponse('Zooming to extents failed', _threeDManager.cameraHandler.getPositionAndTarget());
      });
    }

    /** @inheritdoc */
    resetAsync(transition) {
      return _threeDManager.cameraHandler.resetPositionAndTarget(
        transition
      ).then(function (bSuccess) {
        if (bSuccess)
          return _apiResponse(null, _threeDManager.cameraHandler.getPositionAndTarget());
        else
          return _apiResponse('Resetting camera position or target failed', _threeDManager.cameraHandler.getPositionAndTarget());
      });
    }
  }

  return new CameraApi();
};

module.exports = CameraApi;
